import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  type DialogContentProps,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ProblemBanner,
} from '@servicetim/ui/components';
import { LoaderCircle } from 'lucide-react';
import type { FormEventHandler, ReactNode } from 'react';

export interface FormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  /** `<form id>`; the submit button lives in the footer and targets it. */
  formId: string;
  onSubmit: FormEventHandler<HTMLFormElement>;
  submitLabel?: string;
  cancelLabel?: string;
  busy?: boolean;
  /** Server error shown above the form (field errors already mapped are omitted via `omitPaths`). */
  error?: unknown;
  omitPaths?: ReadonlySet<string>;
  size?: DialogContentProps['size'];
  /** Extra footer content, left of the buttons. */
  footer?: ReactNode;
  /** Hide the submit/cancel buttons (read-only dialogs). */
  hideActions?: boolean;
  children: ReactNode;
}

/**
 * Dialog + form + footer for the CRUD dialogs of the tenant/identity/access screens. The caller
 * owns the `react-hook-form` instance and passes `form.handleSubmit(...)` as `onSubmit`.
 */
export function FormDialog({
  open,
  onOpenChange,
  title,
  description,
  formId,
  onSubmit,
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  busy = false,
  error,
  omitPaths,
  size = 'md',
  footer,
  hideActions = false,
  children,
}: FormDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(next) => (busy && !next ? undefined : onOpenChange(next))}>
      <DialogContent size={size}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>
        {error ? <ProblemBanner error={error} omitPaths={omitPaths} size="sm" /> : null}
        <form id={formId} onSubmit={onSubmit} noValidate className="space-y-4">
          {children}
        </form>
        {hideActions ? null : (
          <DialogFooter className="items-center">
            {footer ? <div className="mr-auto text-fg-muted text-sm">{footer}</div> : null}
            <DialogClose asChild>
              <Button variant="outline" disabled={busy}>
                {cancelLabel}
              </Button>
            </DialogClose>
            <Button type="submit" form={formId} disabled={busy}>
              {busy ? <LoaderCircle className="animate-spin" /> : null}
              {submitLabel}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
